/**
 * infiniteScroll — carica pagine successive del catalogo via API.
 *
 * Pagina 1 è SSR (SEO). Le pagine successive vengono caricate via fetch
 * quando l'utente scrolla fino al sentinel (x-intersect).
 *
 * Uso nel template:
 *   <div x-data="infiniteScroll({ page: 1, pageCount: 5, pageSize: 24, total: 120 })">
 */
export function registerInfiniteScroll(Alpine) {
    Alpine.data('infiniteScroll', (config = {}) => ({
        currentPage: config.page || 1,
        pageCount: config.pageCount || 1,
        pageSize: config.pageSize || 24,
        totalItems: config.total || 0,
        countFad: config.countFad || 0,
        loadedCourses: [],
        loading: false,
        error: false,
        _abortController: null,

        get hasMore() {
            return this.currentPage < this.pageCount;
        },

        /**
         * Costruisce l'URL API per la pagina richiesta.
         * Usa i parametri iniettati da PHP (window.IKOS.catalogApiQuery)
         * per evitare di duplicare la logica di mapping.
         */
        buildApiUrl(page) {
            const baseQuery = window.IKOS?.catalogApiQuery || {};
            const params = new URLSearchParams();

            for (const [key, value] of Object.entries(baseQuery)) {
                params.set(key, value);
            }
            params.set('page', page);
            params.set('page_size', this.pageSize);

            const apiBase = window.IKOS?.apiBase || '';
            return `${apiBase}/v1/courses?${params.toString()}`;
        },

        /**
         * Header di autenticazione per il fetch delle pagine successive.
         *
         * La pagina 1 è SSR e l'API riceve l'Auth-Token via ApiClient.php,
         * quindi applica gli sconti customer (es. Global Card → prezzo 0).
         * Le pagine 2+ sono fetch lato browser: se NON inviamo l'Auth-Token,
         * l'API tratta la richiesta come anonima e mostra il prezzo pieno.
         *
         * Stesso schema usato in stores/cart.js: si replica per coerenza.
         */
        buildApiHeaders() {
            const headers = { 'Accept': 'application/json' };

            // authToken può valere `true` (placeholder non-auth): in quel caso
            // non è un token reale e non va inviato.
            const authToken = window.IKOS?.authToken;
            if (authToken && authToken !== true) {
                headers['Auth-Token'] = authToken;
            }

            // Continuità della sessione guest tra SSR e fetch lato browser.
            const guestId = window.IKOS?.guestId;
            if (guestId) {
                headers['X-Guest-Id'] = guestId;
            }

            return headers;
        },

        async loadMore() {
            if (this.loading || !this.hasMore) return;

            this.loading = true;
            this.error = false;

            if (this._abortController) this._abortController.abort();
            this._abortController = new AbortController();

            const nextPage = this.currentPage + 1;

            try {
                const url = this.buildApiUrl(nextPage);
                const res = await fetch(url, {
                    headers: this.buildApiHeaders(),
                    signal: this._abortController.signal,
                });

                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const data = await res.json();
                const items = data?._embedded?.items || [];

                this.loadedCourses.push(...items);
                this.currentPage = nextPage;
                this.pageCount = data.page_count || this.pageCount;
                this.totalItems = data.total_items || this.totalItems;

                // Il tracking (hooks.js) ascolta questo evento per il view_item_list
                // delle pagine caricate via scroll (la pagina 1 è tracciata lato server).
                window.dispatchEvent(new CustomEvent('ik:catalog-loaded', {
                    detail: { items, page: nextPage, pageSize: this.pageSize },
                }));
            } catch (e) {
                if (e.name !== 'AbortError') {
                    this.error = true;
                }
            } finally {
                this.loading = false;
            }
        },

        /** URL scheda corso: /{urlkey}.html */
        courseUrl(course) {
            return '/' + (course.urlkey || '') + '.html';
        },

        /** Prezzo formattato (49,90) */
        formatPrice(price) {
            return new Intl.NumberFormat('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(price);
        },

        /** ucfirst + lowercase */
        capitalize(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        },

        /**
         * Badge luogo/data dei corsi residenziali, allineato a partials/course-card.php:
         * "Torino, il 19/09/2026" oppure "Torino, dal 15 al 17/05/2026".
         * Senza città il corso non ha una sede fisica (es. webinar) → "Online".
         */
        locationLabel(course) {
            const city = (course.city || '').trim();
            const parts = [city ? this.capitalize(city) : 'Online'];
            if (course.start_at) {
                const [sy, sm, sd] = course.start_at.split('-');
                const end = course.end_at ? course.end_at.split('-') : null;
                if (end && course.end_at > course.start_at) {
                    const [ey, em, ed] = end;
                    const sameYear  = sy === ey;
                    const sameMonth = sameYear && sm === em;
                    const from = sameMonth ? sd : (sameYear ? `${sd}/${sm}` : `${sd}/${sm}/${sy}`);
                    parts.push(`dal ${from} al ${ed}/${em}/${ey}`);
                } else {
                    parts.push(`il ${sd}/${sm}/${sy}`);
                }
            }
            return parts.join(', ');
        },

        /** Icona del badge luogo: sede fisica → map-pin, altrimenti monitor */
        locationIcon(course) {
            return (course.city || '').trim() ? '#ik-i-map-pin' : '#ik-i-monitor';
        },

        /** Data in italiano: 'YYYY-MM-DD' → 'gg mese aaaa' */
        formatDateIT(dateStr) {
            if (!dateStr) return '';
            const mesi = ['','gennaio','febbraio','marzo','aprile','maggio','giugno',
                           'luglio','agosto','settembre','ottobre','novembre','dicembre'];
            const [y, m, d] = dateStr.split('-').map(Number);
            return d + ' ' + (mesi[m] || '') + ' ' + y;
        },
    }));
}
